import {
  ClerkProvider,
  OrganizationList,
  OrganizationProfile,
  OrganizationSwitcher,
  Show,
  SignIn,
  SignOutButton,
  SignUp,
  UserButton,
  UserProfile,
  useAuth,
  useOrganization,
  useOrganizationList,
  useReverification,
  useUser,
} from "@clerk/react";
import { esES } from "@clerk/localizations/es-ES";
import {
  LEGAL_POLICY_VERSIONS,
  legalPolicyEffectiveDate,
} from "@tribunalea/shared";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  hasRestoredPreferenceSession,
  markPreferenceSessionRestored,
  mergeSuitePreferences,
  normalizeSuitePreferences,
  sameSuitePreferences,
  SUITE_PREFERENCES_METADATA_KEY,
  workspacePreference,
} from "./suite-preferences.js";
import "./styles.css";

const SupportSessionContext = createContext({
  active: null,
  loading: false,
  endSession: async () => {},
});

const SuitePreferencesContext = createContext({
  freshSignIn: false,
  preferences: normalizeSuitePreferences(null),
  ready: false,
  updatePreferences: async () => null,
});

const tribunaleaLocalization = {
  ...esES,
  signIn: {
    ...esES.signIn,
    start: {
      ...esES.signIn.start,
      actionText: "¿Es tu primera vez en Tribunalea?",
      actionLink: "Crear cuenta",
      titleCombined: "Entrar o crear una cuenta",
      subtitleCombined: "para continuar a {{applicationName}}",
    },
  },
  unstable__errors: {
    ...esES.unstable__errors,
    form_identifier_not_found:
      "No encontramos una cuenta con este correo. Si es tu primera vez, elegí «Crear cuenta» abajo.",
  },
};

function SuitePreferenceLayer({ children }) {
  const { isLoaded, isSignedIn, orgId, sessionId } = useAuth();
  const { isLoaded: isOrganizationListLoaded, setActive } = useOrganizationList();
  const { isLoaded: isUserLoaded, user } = useUser();
  const [ready, setReady] = useState(false);
  const [freshSignIn, setFreshSignIn] = useState(false);
  const [preferences, setPreferences] = useState(() => (
    normalizeSuitePreferences(null)
  ));
  const restoreOperationRef = useRef(null);
  const updateQueueRef = useRef(Promise.resolve());
  const userRef = useRef(user || null);
  userRef.current = user || null;

  const updatePreferences = useCallback((patch) => {
    const update = async () => {
      const currentUser = userRef.current;
      if (!currentUser) return null;
      const current = normalizeSuitePreferences(
        currentUser.unsafeMetadata?.[SUITE_PREFERENCES_METADATA_KEY],
      );
      const next = mergeSuitePreferences(current, patch);
      if (sameSuitePreferences(current, next)) {
        setPreferences((previous) => mergeSuitePreferences(previous, patch));
        return next;
      }

      const metadataPatch = { version: 1 };
      if (Object.hasOwn(patch || {}, "workspace")) {
        metadataPatch.workspace = next.workspace;
      }
      if (Object.hasOwn(patch || {}, "lastProduct")) {
        metadataPatch.lastProduct = next.lastProduct;
      }
      const updatedUser = typeof currentUser.updateMetadata === "function"
        ? await currentUser.updateMetadata({
            unsafeMetadata: {
              [SUITE_PREFERENCES_METADATA_KEY]: metadataPatch,
            },
          })
        : await currentUser.update({
            unsafeMetadata: {
              ...(currentUser.unsafeMetadata || {}),
              [SUITE_PREFERENCES_METADATA_KEY]: next,
            },
          });
      userRef.current = updatedUser || currentUser;
      setPreferences((previous) => mergeSuitePreferences(previous, patch));
      return next;
    };

    updateQueueRef.current = updateQueueRef.current
      .catch(() => null)
      .then(update);
    return updateQueueRef.current;
  }, []);

  useEffect(() => {
    if (!isLoaded) return undefined;
    if (!isSignedIn) {
      restoreOperationRef.current = null;
      setFreshSignIn(false);
      setReady(true);
      return undefined;
    }
    if (!sessionId || !isOrganizationListLoaded || !isUserLoaded || !user) {
      setReady(false);
      return undefined;
    }
    if (restoreOperationRef.current?.sessionId === sessionId && ready) {
      return undefined;
    }

    let operation = restoreOperationRef.current;
    if (operation?.sessionId !== sessionId) {
      const storedPreferences = normalizeSuitePreferences(
        user.unsafeMetadata?.[SUITE_PREFERENCES_METADATA_KEY],
      );
      const alreadyRestored = hasRestoredPreferenceSession(sessionId);
      operation = {
        sessionId,
        promise: (async () => {
          let restoredPreferences = storedPreferences;
          if (!alreadyRestored && storedPreferences.workspace) {
            const organization = storedPreferences.workspace.type === "organization"
              ? storedPreferences.workspace.organizationId
              : null;
            if ((organization || null) !== (orgId || null)) {
              try {
                await setActive({ organization });
              } catch {
                await setActive({ organization: null }).catch(() => null);
                restoredPreferences = mergeSuitePreferences(storedPreferences, {
                  workspace: workspacePreference(null),
                });
              }
            }
          }
          markPreferenceSessionRestored(sessionId);
          return {
            freshSignIn: !alreadyRestored,
            preferences: restoredPreferences,
          };
        })(),
      };
      restoreOperationRef.current = operation;
    }

    let active = true;
    setReady(false);
    operation.promise.then((result) => {
      if (!active) return;
      setPreferences(result.preferences);
      setFreshSignIn(result.freshSignIn);
      setReady(true);
    });
    return () => {
      active = false;
    };
  }, [
    isLoaded,
    isOrganizationListLoaded,
    isSignedIn,
    isUserLoaded,
    orgId,
    ready,
    sessionId,
    setActive,
    user,
  ]);

  useEffect(() => {
    if (!ready || !isSignedIn || !user) return;
    updatePreferences({
      workspace: workspacePreference(orgId),
    }).catch(() => null);
  }, [isSignedIn, orgId, ready, updatePreferences, user]);

  const value = useMemo(() => ({
    freshSignIn,
    preferences,
    ready,
    updatePreferences,
  }), [freshSignIn, preferences, ready, updatePreferences]);

  if (isSignedIn && !ready) {
    return (
      <div className="auth-loading" role="status">
        Recuperando tu último espacio…
      </div>
    );
  }

  return (
    <SuitePreferencesContext.Provider value={value}>
      {children}
    </SuitePreferencesContext.Provider>
  );
}

function SupportImpersonationBanner() {
  const { active, endSession, ending } = useContext(SupportSessionContext);

  if (!active) return null;

  return (
    <aside className="support-impersonation-banner" role="status">
      <span className="support-impersonation-banner__dot" aria-hidden="true" />
      <span>
        <strong>Modo soporte</strong>
        {" · "}
        Estás usando la cuenta de {active.subjectName}
        {active.workspaceLabel ? ` · ${active.workspaceLabel}` : ""}.
        {" "}
        Todas las acciones quedan auditadas.
      </span>
      <button
        type="button"
        disabled={ending}
        onClick={endSession}
      >
        {ending ? "Finalizando…" : "Finalizar sesión"}
      </button>
    </aside>
  );
}

function legalPolicyUrl(pathname) {
  const hostname = globalThis.location?.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `http://localhost:3000${pathname}`;
  }
  return `https://tribunalea.com${pathname}`;
}

function legalPolicyVersionLabel(version) {
  const effectiveDate = legalPolicyEffectiveDate(version);
  if (!effectiveDate) return version;
  return new Intl.DateTimeFormat("es-AR", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
    year: "numeric",
  }).format(new Date(`${effectiveDate}T00:00:00Z`));
}

function LegalPolicyAcceptanceLayer({ apiUrl, children }) {
  const { getToken, isLoaded, isSignedIn, sessionId } = useAuth();
  const [phase, setPhase] = useState("checking");
  const [status, setStatus] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [securityAccepted, setSecurityAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!isLoaded) return undefined;
    if (!isSignedIn) {
      setPhase("accepted");
      setStatus(null);
      return undefined;
    }

    const controller = new AbortController();
    setPhase("checking");
    setSubmissionError("");
    getToken()
      .then((token) => fetch(`${apiUrl}/api/legal-policies`, {
        credentials: "include",
        headers: token ? { authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      }))
      .then(async (response) => {
        if (!response.ok) throw new Error("policy_status_unavailable");
        return (await response.json()).data;
      })
      .then((nextStatus) => {
        setStatus(nextStatus);
        setPrivacyAccepted(false);
        setSecurityAccepted(false);
        setPhase(nextStatus.required ? "required" : "accepted");
      })
      .catch((error) => {
        if (error.name !== "AbortError") setPhase("error");
      });

    return () => controller.abort();
  }, [apiUrl, getToken, isLoaded, isSignedIn, revision, sessionId]);

  const acceptPolicies = useCallback(async () => {
    if (!privacyAccepted || !securityAccepted || submitting) return;
    setSubmitting(true);
    setSubmissionError("");
    try {
      const token = await getToken();
      const versions = status?.currentVersions || LEGAL_POLICY_VERSIONS;
      const response = await fetch(`${apiUrl}/api/legal-policies/acceptance`, {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          privacyVersion: versions.privacy,
          securityVersion: versions.security,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        if (payload?.code === "legal_policy_versions_outdated") {
          setSubmissionError(
            "Las políticas se actualizaron mientras las revisabas. Recargá la página para ver la versión vigente.",
          );
          return;
        }
        throw new Error("policy_acceptance_unavailable");
      }
      const nextStatus = (await response.json()).data;
      setStatus(nextStatus);
      setPhase(nextStatus.required ? "required" : "accepted");
    } catch {
      setSubmissionError(
        "No pudimos guardar tu aceptación. Revisá tu conexión e intentá nuevamente.",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    apiUrl,
    getToken,
    privacyAccepted,
    securityAccepted,
    status,
    submitting,
  ]);

  if (!isLoaded || (isSignedIn && phase === "checking")) {
    return (
      <div className="auth-loading" role="status">
        Verificando las políticas vigentes…
      </div>
    );
  }

  if (!isSignedIn || phase === "accepted") return children;

  if (phase === "error") {
    return (
      <main className="legal-policy-screen">
        <section className="legal-policy-card legal-policy-card--error" role="alert">
          <span className="legal-policy-eyebrow">ACCESO SEGURO</span>
          <h1>No pudimos verificar las políticas</h1>
          <p>
            Necesitamos confirmar que aceptaste la versión vigente antes de
            darte acceso al sistema.
          </p>
          <div className="legal-policy-actions">
            <button type="button" onClick={() => setRevision((value) => value + 1)}>
              Reintentar
            </button>
            <SignOutButton>
              <button type="button" className="legal-policy-secondary-button">
                Cerrar sesión
              </button>
            </SignOutButton>
          </div>
        </section>
      </main>
    );
  }

  const versions = status?.currentVersions || LEGAL_POLICY_VERSIONS;
  const canAccept = privacyAccepted && securityAccepted && !submitting;

  return (
    <main className="legal-policy-screen">
      <section className="legal-policy-card" aria-labelledby="legal-policy-title">
        <header className="legal-policy-heading">
          <span className="legal-policy-eyebrow">ANTES DE CONTINUAR</span>
          <h1 id="legal-policy-title">Revisá y aceptá nuestras políticas</h1>
          <p>
            Para proteger tu información y explicarte cómo la usamos,
            necesitás revisar y aceptar las versiones vigentes.
          </p>
        </header>

        <div className="legal-policy-documents">
          <a
            href={legalPolicyUrl("/privacidad")}
            target="_blank"
            rel="noreferrer"
          >
            <span>
              <strong>Política de Privacidad</strong>
              <small>Cómo tratamos y protegemos tus datos personales.</small>
            </span>
            <span className="legal-policy-document-meta">
              Vigente desde el {legalPolicyVersionLabel(versions.privacy)}
              {` · versión ${versions.privacy}`}
              <b aria-hidden="true">↗</b>
            </span>
          </a>
          <a
            href={legalPolicyUrl("/seguridad")}
            target="_blank"
            rel="noreferrer"
          >
            <span>
              <strong>Política de Seguridad</strong>
              <small>Las medidas que aplicamos para cuidar tu cuenta.</small>
            </span>
            <span className="legal-policy-document-meta">
              Vigente desde el {legalPolicyVersionLabel(versions.security)}
              {` · versión ${versions.security}`}
              <b aria-hidden="true">↗</b>
            </span>
          </a>
        </div>

        <div className="legal-policy-consents">
          <label>
            <input
              type="checkbox"
              checked={privacyAccepted}
              onChange={(event) => setPrivacyAccepted(event.target.checked)}
            />
            <span>
              Leí y acepto la <a href={legalPolicyUrl("/privacidad")} target="_blank" rel="noreferrer">Política de Privacidad</a>.
            </span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={securityAccepted}
              onChange={(event) => setSecurityAccepted(event.target.checked)}
            />
            <span>
              Leí y acepto la <a href={legalPolicyUrl("/seguridad")} target="_blank" rel="noreferrer">Política de Seguridad</a>.
            </span>
          </label>
        </div>

        {submissionError && (
          <p className="legal-policy-error" role="alert">{submissionError}</p>
        )}

        <div className="legal-policy-actions">
          <button type="button" disabled={!canAccept} onClick={acceptPolicies}>
            {submitting ? "Guardando aceptación…" : "Aceptar y continuar"}
          </button>
          <SignOutButton>
            <button type="button" className="legal-policy-secondary-button">
              Cerrar sesión
            </button>
          </SignOutButton>
        </div>
      </section>
    </main>
  );
}

function SupportSessionLayer({ apiUrl, children }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState(false);

  const request = useCallback(async (method = "GET") => {
    const token = await getToken();
    const response = await fetch(`${apiUrl}/api/support/impersonation`, {
      method,
      credentials: "include",
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error("support_session_unavailable");
    if (response.status === 204) return null;
    return (await response.json()).data;
  }, [apiUrl, getToken]);

  useEffect(() => {
    if (!isLoaded) return undefined;
    if (!isSignedIn) {
      setActive(null);
      setLoading(false);
      return undefined;
    }
    let mounted = true;
    request()
      .then((status) => {
        if (mounted) setActive(status?.active || null);
      })
      .catch(() => {
        if (mounted) setActive(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isLoaded, isSignedIn, request]);

  const endSession = useCallback(async () => {
    setEnding(true);
    try {
      await request("DELETE");
      setActive(null);
      window.location.reload();
    } finally {
      setEnding(false);
    }
  }, [request]);

  const value = useMemo(() => ({
    active,
    loading,
    ending,
    endSession,
  }), [active, endSession, ending, loading]);

  return (
    <SupportSessionContext.Provider value={value}>
      <SupportImpersonationBanner />
      {children}
    </SupportSessionContext.Provider>
  );
}

export function TribunaleaAuthProvider({
  publishableKey,
  children,
  demoFallback,
  supportEmail = "soporte@tribunalea.com",
  apiUrl = "http://localhost:4000",
}) {
  if (!publishableKey) return demoFallback;

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      localization={tribunaleaLocalization}
      signInUrl="/"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      supportEmail={supportEmail}
    >
      <SuitePreferenceLayer>
        <LegalPolicyAcceptanceLayer apiUrl={apiUrl}>
          <SupportSessionLayer apiUrl={apiUrl}>
            {children}
          </SupportSessionLayer>
        </LegalPolicyAcceptanceLayer>
      </SuitePreferenceLayer>
    </ClerkProvider>
  );
}

export function AuthPortal({
  productName,
  headline,
  description,
  proofPoints = [],
  tools = [],
  legalLinks = [
    { label: "Privacidad", href: "https://tribunalea.com/privacidad" },
    { label: "Cookies", href: "https://tribunalea.com/privacidad#cookies" },
    { label: "Seguridad", href: "https://tribunalea.com/seguridad" },
  ],
}) {
  return (
    <div className="auth-screen">
      <div className={`auth-brand-panel${tools.length ? " auth-brand-panel--ecosystem" : ""}`}>
        <span className="auth-eyebrow">TRIBUNALEA · {productName}</span>
        <h1>{headline}</h1>
        <p>{description}</p>
        {tools.length > 0 && (
          <section className="auth-tools" aria-labelledby="auth-tools-title">
            <h2 id="auth-tools-title">El ecosistema Tribunalea</h2>
            <div className="auth-tool-grid">
              {tools.map((tool) => (
                <article className="auth-tool" key={tool.name}>
                  <div className="auth-tool-heading">
                    <strong>{tool.name}</strong>
                    <span>{tool.audience}</span>
                  </div>
                  <p>{tool.description}</p>
                </article>
              ))}
            </div>
          </section>
        )}
        <div className="auth-proof">
          {proofPoints.map((point) => (
            <span key={point}>{point}</span>
          ))}
        </div>
      </div>
      <div className="auth-form-panel">
        <SignIn
          routing="hash"
          fallbackRedirectUrl="/"
          signUpFallbackRedirectUrl="/"
          appearance={{
            variables: {
              colorPrimary: "#15936f",
              colorText: "#1f302e",
              colorBackground: "#ffffff",
              borderRadius: "0.75rem",
            },
            elements: {
              cardBox: { boxShadow: "none" },
              card: {
                boxShadow: "0 18px 50px rgba(25, 52, 47, 0.10)",
                border: "1px solid #dde6e3",
              },
            },
          }}
        />
        {legalLinks.length > 0 && (
          <nav className="auth-legal-links" aria-label="Información legal">
            {legalLinks.map((link) => (
              <a key={link.href} href={link.href} target="_blank" rel="noreferrer">
                {link.label}
              </a>
            ))}
          </nav>
        )}
      </div>
    </div>
  );
}

export function InviteAcceptancePortal({ productName = "CUENTA" }) {
  const hasTicket = new URLSearchParams(window.location.search).has(
    "__clerk_ticket",
  );

  return (
    <div className="auth-screen">
      <div className="auth-brand-panel">
        <span className="auth-eyebrow">TRIBUNALEA · {productName}</span>
        <h1>Te damos la bienvenida a Tribunalea.</h1>
        <p>
          Completá tu cuenta personal para empezar a probar nuestras
          herramientas jurídicas.
        </p>
        <div className="auth-proof">
          <span>Acceso personal por invitación</span>
          <span>Una sola cuenta para todo el ecosistema</span>
          <span>Datos protegidos por Clerk</span>
        </div>
      </div>
      <div className="auth-form-panel">
        {hasTicket ? (
          <SignUp
            routing="hash"
            fallbackRedirectUrl="/"
            signInFallbackRedirectUrl="/"
            appearance={{
              variables: {
                colorPrimary: "#15936f",
                colorText: "#1f302e",
                colorBackground: "#ffffff",
                borderRadius: "0.75rem",
              },
              elements: {
                cardBox: { boxShadow: "none" },
                card: {
                  boxShadow: "0 18px 50px rgba(25, 52, 47, 0.10)",
                  border: "1px solid #dde6e3",
                },
              },
            }}
          />
        ) : (
          <div className="invite-invalid-card" role="alert">
            <span className="auth-eyebrow">INVITACIÓN REQUERIDA</span>
            <h2>Este enlace no es válido.</h2>
            <p>
              Para crear una cuenta necesitás abrir el enlace personal que
              recibiste por email.
            </p>
            <a href="/">Volver a iniciar sesión</a>
          </div>
        )}
      </div>
    </div>
  );
}

export function AuthenticatedArea({ signedOut, children }) {
  return (
    <Show when="signed-in" fallback={signedOut}>
      {children}
    </Show>
  );
}

export function SuiteAccountControls() {
  const { active, loading } = useContext(SupportSessionContext);
  return (
    <div className="clerk-account-controls">
      {loading ? (
        <span className="support-workspace-indicator" aria-label="Verificando espacio">
          Verificando espacio…
        </span>
      ) : active ? (
        <span className="support-workspace-indicator" title={active.workspaceId}>
          {active.workspaceLabel}
        </span>
      ) : (
        <OrganizationSwitcher
          hidePersonal={false}
          afterCreateOrganizationUrl="/"
          afterSelectOrganizationUrl="/"
        />
      )}
      <UserButton />
    </div>
  );
}

function SuiteAccountMenuAvatar({ imageUrl, label }) {
  return (
    <span className="clerk-account-menu-avatar" aria-hidden="true">
      {imageUrl ? (
        <img src={imageUrl} alt="" draggable="false" />
      ) : (
        <span>{label?.trim()?.charAt(0)?.toUpperCase() || "T"}</span>
      )}
    </span>
  );
}

export function SuiteAccountMenuControls() {
  const { active, loading } = useContext(SupportSessionContext);
  const { isLoaded: isOrganizationLoaded, organization } = useOrganization();
  const { isLoaded: isUserLoaded, user } = useUser();
  const userLabel = user?.fullName
    || user?.primaryEmailAddress?.emailAddress
    || "Cuenta personal";
  const workspaceLabel = loading
    ? "Verificando espacio…"
    : active?.workspaceLabel || organization?.name || "Personal";
  const workspaceImage = organization?.imageUrl
    || (!organization ? user?.imageUrl : "")
    || "";
  const controlsReady = isOrganizationLoaded && isUserLoaded;

  return (
    <div className="clerk-account-menu-controls">
      <div className={`clerk-account-menu-row ${
        loading || active || !controlsReady ? "is-static" : ""
      }`}>
        <SuiteAccountMenuAvatar
          imageUrl={workspaceImage}
          label={workspaceLabel}
        />
        <span className="clerk-account-menu-copy">
          <strong>{workspaceLabel}</strong>
          <small>Espacio de trabajo</small>
        </span>
        {!loading && !active && controlsReady && (
          <div className="clerk-account-menu-trigger">
            <OrganizationSwitcher
              hidePersonal={false}
              afterCreateOrganizationUrl="/"
              afterSelectOrganizationUrl="/"
            />
          </div>
        )}
      </div>
      <div className="clerk-account-menu-row">
        <SuiteAccountMenuAvatar imageUrl={user?.imageUrl || ""} label={userLabel} />
        <span className="clerk-account-menu-copy">
          <strong>Sesión</strong>
          <small>{userLabel}</small>
        </span>
        <div className="clerk-account-menu-trigger">
          <UserButton />
        </div>
      </div>
    </div>
  );
}

export function useSuiteAccountAvatar() {
  const { isLoaded: isOrganizationLoaded, organization } = useOrganization();
  const { isLoaded: isUserLoaded, user } = useUser();

  if (!isOrganizationLoaded || !isUserLoaded) return null;

  if (organization) {
    return {
      imageUrl: organization.imageUrl || "",
      label: organization.name || "Organización",
      type: "organization",
    };
  }

  return {
    imageUrl: user?.imageUrl || "",
    label: user?.fullName
      || user?.primaryEmailAddress?.emailAddress
      || "Cuenta personal",
    type: "personal",
  };
}

export function useSuitePreferences() {
  return useContext(SuitePreferencesContext);
}

export function useRememberSuiteProduct(productId, { enabled = true } = {}) {
  const { ready, updatePreferences } = useSuitePreferences();

  useEffect(() => {
    if (!enabled || !ready) return;
    updatePreferences({ lastProduct: productId }).catch(() => null);
  }, [enabled, productId, ready, updatePreferences]);
}

export function SuiteOrganizationProfile() {
  return <OrganizationProfile routing="hash" />;
}

export function SuiteTeamWorkspace() {
  const { isLoaded, organization } = useOrganization();

  if (!isLoaded) {
    return (
      <div className="auth-loading" role="status">
        Preparando tus espacios de trabajo…
      </div>
    );
  }

  if (organization) return <OrganizationProfile routing="hash" />;

  return (
    <div className="personal-workspace">
      <div className="personal-workspace-copy">
        <span className="auth-eyebrow">ESPACIO PERSONAL</span>
        <h2>Estás trabajando de forma independiente</h2>
        <p>
          Tus alertas son privadas. Si más adelante querés colaborar, podés crear
          un equipo o aceptar una invitación sin perder tu espacio personal.
        </p>
      </div>
      <OrganizationList
        hidePersonal={false}
        afterCreateOrganizationUrl="/equipo"
        afterSelectOrganizationUrl="/equipo"
      />
    </div>
  );
}

export function SuiteUserProfile() {
  return <UserProfile routing="hash" />;
}

export function useAccessToken() {
  const { getToken } = useAuth();
  return getToken;
}

export function useTribunaleaAccess({
  apiUrl = "http://localhost:4000",
} = {}) {
  const { getToken, isLoaded, isSignedIn, orgId } = useAuth();
  const [state, setState] = useState({
    access: null,
    error: null,
    loading: true,
  });
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!isLoaded) return undefined;
    if (!isSignedIn) {
      setState({ access: null, error: null, loading: false });
      return undefined;
    }

    const controller = new AbortController();
    setState({ access: null, error: null, loading: true });
    getToken()
      .then((token) => fetch(`${apiUrl}/api/access`, {
        credentials: "include",
        signal: controller.signal,
        headers: token ? { authorization: `Bearer ${token}` } : {},
      }))
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(
            payload.error || "No pudimos verificar los accesos del espacio.",
          );
          error.code = payload.code;
          throw error;
        }
        setState({ access: payload.data, error: null, loading: false });
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setState({ access: null, error, loading: false });
        }
      });

    return () => controller.abort();
  }, [apiUrl, getToken, isLoaded, isSignedIn, orgId, revision]);

  const reload = useCallback(() => setRevision((current) => current + 1), []);
  return { ...state, reload };
}

export {
  OrganizationList,
  OrganizationProfile,
  OrganizationSwitcher,
  Show,
  SignIn,
  SignUp,
  UserButton,
  UserProfile,
  useAuth,
  useOrganization,
  useReverification,
  useUser,
};
